import { localizedAccountDisplayName } from "../utils/localizedAccountDisplayName";
import type { ParseKeys } from "i18next";
import { useTranslation } from "react-i18next";
import React, { useEffect, useMemo, useState } from "react";
import { apiGet, apiPost, apiPut, apiDelete } from "../api/client";
import { ArrowLeftIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { useNavigate, useParams } from "react-router-dom";
import { companyRoute } from "../utils/routeHelpers";
import { Button } from "../components/Button";
import { A } from "../styles/appleClasses";

type Account = {
  id: number;
  name: string;
  type: string;
  kind?: string | null;
  initial_balance?: number;
  is_system?: boolean | null;
};

type Mode = "page" | "inlay";

type Props = {
  mode?: Mode;
  title?: string;
  companyId?: string;
  onClose?: () => void;
  onCreated?: (cat: Account) => void;
};

// Local display keys are distinct from untranslated server/network messages.
type ExpenseMessage = string | { key: ParseKeys };
const localMessage = (key: ParseKeys): ExpenseMessage => ({ key });

export default function EditExpenseCategories({
  mode = "page",
  title: titleProp,
  companyId,
  onClose,
  onCreated,
}: Props) {
  const { t } = useTranslation();
  const displayMessage = (message: ExpenseMessage) => typeof message === "string" ? message : t(message.key);
  const [categories, setCategories] = useState<Account[]>([]);
  const [newName, setNewName] = useState("");
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editName, setEditName] = useState("");
  const [error, setError] = useState<ExpenseMessage | null>(null);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [savingId, setSavingId] = useState<number | null>(null);

  const navigate = useNavigate();
  const { company_id: routeCompanyId } = useParams<{ company_id: string }>();
  const effectiveCompanyId = companyId || routeCompanyId;

  const isInlay = mode === "inlay";

  const lower = (s?: string | null) => (s || "").toLowerCase();
  const isExpense = (a: Account) => lower(a.type) === "expense" && !a.is_system;
  const byName = (a: Account, b: Account) => a.name.localeCompare(b.name);

  async function fetchCategories() {
    setLoading(true);
    setError(null);
    try {
      const rows = await apiGet<Account[]>("/accounts");
      setCategories((rows || []).filter(isExpense).sort(byName));
    } catch (e: any) {
      setCategories([]);
      setError(e?.message || localMessage("expenses.errors.loadCategories"));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Lock background scroll in modal mode
  useEffect(() => {
    if (!isInlay) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isInlay]);

  // ESC to close in modal mode
  useEffect(() => {
    if (!isInlay) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isInlay, onClose]);

  const labelCls = `block mb-1 ${A.label}`;

  async function handleAdd(e: React.FormEvent) {
    e.preventDefault();
    e.stopPropagation();

    const trimmed = newName.trim();
    if (!trimmed) return;

    setError(null);
    try {
      const created = await apiPost<Account>("/accounts", {
        name: trimmed,
        type: "expense",
        kind: "expense",
      });

      setNewName("");

      if (onCreated && created && (created as any).id) {
        onCreated(created);
      }

      await fetchCategories();

      if (isInlay) onClose?.();
    } catch {
      setError(localMessage("expenses.errors.addCategory"));
    }
  }

  function startEdit(id: number, name: string) {
    setEditingId(id);
    setEditName(name);
    setError(null);
  }

  function cancelEdit() {
    setEditingId(null);
    setEditName("");
    setError(null);
  }

  async function handleEdit(id: number) {
    const trimmed = editName.trim();
    if (!trimmed) {
      setError(localMessage("expenses.errors.blankName"));
      return;
    }

    setSavingId(id);
    setError(null);
    try {
      await apiPut(`/accounts/${id}`, {
        name: trimmed,
        type: "expense",
        kind: "expense",
      });
      setEditingId(null);
      setEditName("");
      await fetchCategories();
    } catch {
      setError(localMessage("expenses.errors.updateCategory"));
    } finally {
      setSavingId(null);
    }
  }

  async function handleDelete(id: number) {
    if (!window.confirm(t("expenses.errors.confirmDeleteCategory"))) return;

    setDeletingId(id);
    setError(null);
    try {
      await apiDelete(`/accounts/${id}`);
      await fetchCategories();
    } catch (e: any) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 409)
        setError(detail || localMessage("expenses.errors.categoryInUse"));
      else if (status === 403)
        setError(detail || localMessage("expenses.errors.categoryForbidden"));
      else if (status === 404) setError(localMessage("expenses.errors.categoryNotFound"));
      else setError(localMessage("expenses.errors.deleteCategory"));
    } finally {
      setDeletingId(null);
    }
  }

  const title = useMemo(() => titleProp || t("expenses.categories.title"), [titleProp, t]);

  const closeAction = () => {
    if (isInlay) onClose?.();
    else navigate(companyRoute(effectiveCompanyId, "/settings"));
  };

  const backAction = () => {
    if (isInlay) onClose?.();
    else navigate(-1);
  };

  const header = (
    <div
      className={
        isInlay
          ? "sticky top-0 z-20 bg-white border-b border-gray-100 px-3 sm:px-4 py-3 flex items-center"
          : "sticky top-0 z-30 bg-gray-50 w-full max-w-md flex items-center border-b border-gray-100 px-2 pb-2 mb-2"
      }
    >
      <button
        onClick={backAction}
        className={`${A.iconBtn} inline-flex items-center justify-center hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-appleblueD`}
        aria-label={t("directory.common.back")}
        type="button"
      >
        <ArrowLeftIcon className="w-6 h-6 text-gray-400 hover:text-gray-600 transition" />
      </button>

      <h2 className={`flex-1 text-center ${A.title}`}>{title}</h2>

      <button
        onClick={closeAction}
        className={`${A.iconBtn} inline-flex items-center justify-center hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-appleblueD`}
        aria-label={t("directory.common.close")}
        type="button"
      >
        <XMarkIcon className="w-6 h-6 text-gray-400 hover:text-gray-600 transition" />
      </button>
    </div>
  );

  const content = (
    <div className={`${A.card} p-5 sm:p-6`}>
      <form onSubmit={handleAdd} className="mb-4">
        <label className={labelCls}>{t("expenses.categories.addCategory")}</label>
        <div className="flex gap-2">
          <input
            className={`${A.inputBase} flex-1`}
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder={t("expenses.categories.categoryName")}
            maxLength={60}
            onKeyDownCapture={(e) => {
              if (e.key !== "Enter") return;
              if ((e.nativeEvent as any)?.isComposing) return;
              e.preventDefault();
              e.stopPropagation();
            }}
          />
          <Button
            type="submit"
            variant="tertiary"
            minWidth="auto"
            disabled={!newName.trim()}
          >
            {t("expenses.categories.add")}
          </Button>
        </div>
      </form>

      {loading ? (
        <div className="py-8 text-center text-xs text-gray-500">{t("directory.common.loading")}</div>
      ) : categories.length === 0 ? (
        <div className="py-8 text-center text-xs text-gray-500">
          {t("expenses.categories.noCategories")}
        </div>
      ) : (
        <ul className="divide-y divide-gray-100">
          {categories.map((cat) => {
            const isEditing = editingId === cat.id;
            const isDeleting = deletingId === cat.id;
            const isSaving = savingId === cat.id;

            return (
              <li key={cat.id} className="py-3">
                {isEditing ? (
                  <div className="flex items-center gap-2">
                    <input
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      className={`${A.inputBase} flex-1`}
                      autoFocus
                      maxLength={60}
                    />

                    <Button
                      type="button"
                      variant="secondary"
                      minWidth="auto"
                      onClick={cancelEdit}
                      disabled={isSaving}
                    >
                      {t("directory.common.cancel")}
                    </Button>

                    <Button
                      type="button"
                      variant="primary"
                      minWidth="auto"
                      disabled={isSaving}
                      onClick={() => handleEdit(cat.id)}
                    >
                      {isSaving ? t("directory.common.savingEllipsis") : t("directory.common.save")}
                    </Button>
                  </div>
                ) : (
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="truncate">{localizedAccountDisplayName(cat, t, cat.name)}</div>
                    </div>

                    <div className="flex items-center gap-2 shrink-0">
                      <Button
                        type="button"
                        variant="secondary"
                        minWidth="auto"
                        onClick={() => startEdit(cat.id, cat.name)}
                        disabled={isDeleting}
                      >
                        {t("directory.common.edit")}
                      </Button>

                      <Button
                        type="button"
                        variant="secondary"
                        minWidth="auto"
                        disabled={isDeleting}
                        onClick={() => handleDelete(cat.id)}
                      >
                        {isDeleting ? t("directory.common.deleting") : t("directory.common.delete")}
                      </Button>
                    </div>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {error && (
        <div className="mt-4 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-[13px] text-red-700">
          {displayMessage(error)}
        </div>
      )}
    </div>
  );

  if (isInlay) {
    return (
      <div
        className="fixed inset-0 z-[1000] bg-black/40"
        role="dialog"
        aria-modal="true"
      >
        <div
          className="absolute inset-0"
          onClick={onClose}
          aria-hidden="true"
        />

        <div className="absolute inset-0 flex items-start justify-center p-2 sm:p-4">
          <div
            className={`${A.card} relative w-full max-w-md p-0 shadow-md flex flex-col overflow-hidden`}
            style={{ maxHeight: "calc(100dvh - 16px)" }}
            onClick={(e) => e.stopPropagation()}
          >
            {header}

            <div
              className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-2 pb-2 sm:px-0 sm:pb-0"
              style={{ WebkitOverflowScrolling: "touch" }}
            >
              <div className="px-2 sm:px-0">{content}</div>
            </div>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div
      className={`min-h-screen bg-gray-50 ${A.body} flex flex-col items-center pt-4 sm:pt-6`}
    >
      {header}
      <div className="w-full max-w-md px-2 pb-10">{content}</div>
    </div>
  );
}
